import { TFunction } from 'i18next';

import {
  HeaderTabs,
  advisorHeaderTabs,
  sboHeaderTabs,
  unauthenticatedHeaderTabs,
} from '_constants/headerConstants';

import { TenantModules, TenantTypeEnum } from '_types/tenant-settings';
import { UnauthHomePageConfigrationDataJSON } from '_types/tenant-settings/tenant-page-configuration/unauth-home-page';
import { UserRolesEnum } from '_types/user.interface';

const filterTabsWithTenantModules = (
  tabs: HeaderTabs[],
  tenantModules: TenantModules,
) =>
  tabs.filter((tab) => {
    // Explanation:
    // - 'sessions' module is related to login functionality.
    // - 'users' module is related to both advisors and login functionality.
    // - Disabling 'sessions' disables login. Since 'users' is tied to both advisors and login, we need to ensure we only disable advisors.
    // - To disable only advisors, we check if the tab's moduleField is 'users' and the path isn't '#'.
    // - If the path is '#', it's an unauthenticated page, so we shouldn't disable it.
    // - If the path is different, it's an authenticated page, and we should disable it if 'sessions' is not present in tenantModules.
    if (
      tab.moduleField === 'users' &&
      tab.path !== '#' &&
      !tenantModules.sessions
    ) {
      return false;
    }
    // Otherwise, include the tab if it doesn't have a moduleField or if tenantModules contains the moduleField
    return !tab.moduleField || tenantModules[tab.moduleField];
  });

export const headerTabsConstantsFn = (
  role: UserRolesEnum | undefined,
  tenantModules: TenantModules | null | undefined,
  t: TFunction,
  tenantType?: TenantTypeEnum,
  pageConfiguration?: UnauthHomePageConfigrationDataJSON | null,
): HeaderTabs[] => {
  let headerTabsList: HeaderTabs[];

  switch (role) {
    case UserRolesEnum.ADVISORY_ORG_ADMIN:
    case UserRolesEnum.ADVISOR:
      headerTabsList = advisorHeaderTabs(t);
      break;
    case UserRolesEnum.SBO:
      headerTabsList = sboHeaderTabs(t);
      break;
    default:
      headerTabsList = unauthenticatedHeaderTabs(
        t,
        tenantType,
        pageConfiguration,
      );
      break;
  }

  if (!tenantModules) {
    return headerTabsList;
  }

  return filterTabsWithTenantModules(headerTabsList, tenantModules);
};
